import "./OpenTraderWelcomeWizardComponent.scss";
import template from "./OpenTraderWelcomeWizardComponent.html";
import {IComponentOptions, IHttpService, IOnDestroy, IScope, IWindowService} from "angular";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {ReplaySubject} from "rxjs";
import {CurrentUser} from "../../../services/user/models/CurrentUser";
import {UserService} from "../../../services/user/UserService";
import {OpenTraderConversionEventService} from "../../../services/open-trader/google/OpenTraderConversionEventService";
import {ThemeType} from "../../../types/UserConfig";
import {VIRTUAL_PAGE_VIEWS} from "../../../types/VirtualPageViews";
import {ThemingStore} from "../../../theming/store/ThemingStore";
import {IModalInstanceService} from "angular-ui-bootstrap";
import {Optional} from "../../../js/util/Optional";

export const OpenTraderWelcomeWizard = "tfOpenTraderWelcomeWizard";

export function openTraderWelcomeWizard(): IComponentOptions {
    return {
        template,
        bindings: {
            modalInstance: "<"
        },
        controllerAs: "ctrl",
        controller: OpenTraderWelcomeWizardComponentController
    };
}

export class OpenTraderWelcomeWizardComponentController implements IOnDestroy {

    /** @input */
    public readonly modalInstance: IModalInstanceService;

    public currentStepNumber = 0;
    public givenName: string | null = null;
    public surname: string | null = null;
    public postcode: string | null = null;
    public state: string | null = null;
    public phone: string | null = null;
    public sophisticatedOrWholesale: boolean;

    public priceInCents = false;
    public theme: ThemeType = "dark";

    private readonly destroyed$ = new ReplaySubject<boolean>(1);
    private hasTrackedStep2 = false;

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService,
                       private readonly userService: UserService,
                       private readonly openTraderConversionEventService: OpenTraderConversionEventService,
                       private readonly themingStore: ThemingStore,
                       private readonly $scope: IScope,
                       private readonly $http: IHttpService,
                       private readonly $window: IWindowService) {

        this.currentUserService
            .current
            .first()
            .subscribe((user) => this.setUpFormData(user));

        this.currentUserService
            .current
            .takeUntil(this.destroyed$)
            .map((user) => user.preferences)
            .map((prefs) => !!prefs.priceInCents)
            .subscribe((pic) => this.$scope.$applyAsync(() => this.priceInCents = pic));

        this.themingStore
            .currentTheme
            .takeUntil(this.destroyed$)
            .subscribe((theme) => this.$scope.$applyAsync(() => this.theme = theme));
    }

    private setUpFormData(user: CurrentUser) {
        if (user.profile.givenName != null) {
            this.givenName = user.profile.givenName;
            this.surname = user.profile.surname;
        } else {
            const fullName = user.shortName;
            const split = fullName.split(" ");
            const givenName = split[0];
            const surnameIndex = Math.max(1, split.length - 1);
            const surname = split[surnameIndex];
            this.givenName = givenName;
            this.surname = surname;
        }

        this.sophisticatedOrWholesale = Optional.of(user)
                                                .map((c) => c.profile)
                                                .map((p) => p.sophisticated)
                                                .orElse(false);
        this.phone = Optional.of(user)
                             .map((c) => c.profile)
                             .map((p) => p.phone)
                             .orNull();

        this.postcode = Optional.of(user)
                                .map((c) => c.profile)
                                .map((p) => p.postCode)
                                .orNull();

        // First time this loads, we'll be on the Personal Information step
        if (this.currentStepNumber === 0) {
            this.openTraderConversionEventService.sendVirtualPageView(VIRTUAL_PAGE_VIEWS.RegisterPersonalInformation);
        }

    }

    public updateTheme(theme: ThemeType) {
        this.currentUserService.updatePreference("theme", theme);
    }

    public updatePriceInCents(priceInCents: boolean) {
        this.currentUserService.updatePreference("priceInCents", priceInCents);
    }

    public updatePhone(value: string | null) {
        this.phone = value;
        this.userService.updateContactNumber(this.phone);
    }

    public nextPage() {
        this.currentStepNumber = this.currentStepNumber + 1;

        // If we're on Step 2, and we're yet to track it this instance, track it
        if (this.currentStepNumber === 1) {
            if (!this.hasTrackedStep2) {
                this.openTraderConversionEventService.sendVirtualPageView(
                    VIRTUAL_PAGE_VIEWS.RegisterTerms
                );
                this.hasTrackedStep2 = true;
            }
        }
    }

    public previousPage() {
        this.currentStepNumber = this.currentStepNumber - 1;
    }

    public submitPersonal() {
        this.userService.updateFullName(this.givenName!, this.surname!);
        this.nextPage();
    }

    public acceptTerms() {
        const demographics = {
            state: this.state,
            postCode: this.postcode,
            sophisticatedOrWholesale: this.sophisticatedOrWholesale
        };

        this.$http.put("/tradefloor/api/user/legal/terms", null);
        this.$http.post("/tradefloor/api/user/demographics", demographics);
        this.$http.put("/tradefloor/api/user/onboard", null);

        this.openTraderConversionEventService.sendVirtualPageView(VIRTUAL_PAGE_VIEWS.FirstTimeDemo);
        this.modalInstance.close();
    }

    public async declineTerms() {
        const response = await this.$http.get("/tradefloor/api/logout");
        this.$window.location.href = response.headers("location") || "/";
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
